import { ROUTERS } from '@/constant';
import { useDocumentTitle } from '@/hooks';
import { MyPageSelectors } from '@/stores/my-page';
import { isMobile } from '@/utils/mobile.util';
import { lazy, memo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import { CreateDetailLayout } from './create-detail-layout';
import { MainLayout } from './main-layout';

const MyPage = lazy(() => import('@/pages/mp1-01'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));
const NM1_01Page = lazy(() => import('@/pages/commons/nm1-01'));
const NM1_02Page = lazy(() => import('@/pages/commons/nm1-02'));
const NM1_03Page = lazy(() => import('@/pages/commons/nm1-03'));
const NP1_01Page = lazy(() => import('@/pages/commons/np1-01'));
const NP1_03Page = lazy(() => import('@/pages/commons/np1-03'));
const BBM1_01Page = lazy(() => import('@/pages/commons/bbm1-01'));
const BBM1_03Page = lazy(() => import('@/pages/commons/bbm1-03'));
const BB1_01Page = lazy(() => import('@/pages/commons/bb1-01'));
const BB1_02Page = lazy(() => import('@/pages/commons/bb1-02'));
const BB1_03Page = lazy(() => import('@/pages/commons/bb1-03'));
const AU1_01Page = lazy(() => import('@/pages/commons/au1-01'));

// systems
const UM1_01Page = lazy(() => import('@/pages/systems/um1-01'));
const UM1_02Page = lazy(() => import('@/pages/systems/um1-02'));
const UM1_03Page = lazy(() => import('@/pages/systems/um1-03'));
const CM1_01Page = lazy(() => import('@/pages/systems/cm1-01'));
const CM1_02Page = lazy(() => import('@/pages/systems/cm1-02'));
const CM1_03Page = lazy(() => import('@/pages/systems/cm1-03'));
const CM2_01Page = lazy(() => import('@/pages/systems/cm2-01'));
const CM2_02Page = lazy(() => import('@/pages/systems/cm2-02'));
const CM2_03Page = lazy(() => import('@/pages/systems/cm2-03'));
const SM1_01Page = lazy(() => import('@/pages/systems/sm1-01'));
const SM1_02Page = lazy(() => import('@/pages/systems/sm1-02'));
const SM1_03Page = lazy(() => import('@/pages/systems/sm1-03'));

// workers
const ED1_01Page = lazy(() => import('@/pages/workers/ed1-01'));
const MM1_01Page = lazy(() => import('@/pages/workers/mm1-01'));
const MM1_02Page = lazy(() => import('@/pages/workers/mm1-02'));
const MM1_03Page = lazy(() => import('@/pages/workers/mm1-03'));
const AR1_01Page = lazy(() => import('@/pages/workers/ar1-01'));
const AR1_02Page = lazy(() => import('@/pages/workers/ar1-02'));
const AR1_03Page = lazy(() => import('@/pages/workers/ar1-03'));
const FR1_01Page = lazy(() => import('@/pages/workers/fr1-01'));
const FR1_02Page = lazy(() => import('@/pages/workers/fr1-02'));
const FR1_03Page = lazy(() => import('@/pages/workers/fr1-03'));

const PM1_01Page = lazy(() => import('@/pages/workers/pm1-01'));
const PM1_02Page = lazy(() => import('@/pages/workers/pm1-02'));
const PM1_03Page = lazy(() => import('@/pages/workers/pm1-03'));
const PM2_01Page = lazy(() => import('@/pages/workers/pm2-01'));
const WC1_01Page = lazy(() => import('@/pages/workers/wc1-01'));
const WC1_02Page = lazy(() => import('@/pages/workers/wc1-02'));
const WC1_03Page = lazy(() => import('@/pages/workers/wc1-03'));
const US1_01Page = lazy(() => import('@/pages/workers/us1-01'));
const LM1_01Page = lazy(() => import('@/pages/workers/lm1-01'));
const LM1_02Page = lazy(() => import('@/pages/workers/lm1-02'));
const LM1_03Page = lazy(() => import('@/pages/workers/lm1-03'));
const RM1_01Page = lazy(() => import('@/pages/workers/rm1-01'));
const RM1_02Page = lazy(() => import('@/pages/workers/rm1-02'));
const RM1_03Page = lazy(() => import('@/pages/workers/rm1-03'));
const FM1_01Page = lazy(() => import('@/pages/workers/fm1-01'));
const BM1_01Page = lazy(() => import('@/pages/workers/bm1-01'));
const BM1_02Page = lazy(() => import('@/pages/workers/bm1-02'));
const BM1_03Page = lazy(() => import('@/pages/workers/bm1-03'));
const DM1_01Page = lazy(() => import('@/pages/workers/dm1-01'));
const AM1_01Page = lazy(() => import('@/pages/workers/am1-01'));
const AM1_03Page = lazy(() => import('@/pages/workers/am1-03'));
const AM3_01Page = lazy(() => import('@/pages/workers/am3-01'));
const PA1_01Page = lazy(() => import('@/pages/workers/pa1-01'));
const PA1_03Page = lazy(() => import('@/pages/workers/pa1-03'));
const PA2_01Page = lazy(() => import('@/pages/workers/pa2-01'));
const PA3_01Page = lazy(() => import('@/pages/workers/pa3-01'));
const PA3_03Page = lazy(() => import('@/pages/workers/pa3-03'));
const PA4_01Page = lazy(() => import('@/pages/workers/pa4-01'));

const LM2_01Page = lazy(
	() => import('@/pages/workers/lm1-03/components/lm2-1')
);
const DM1_02Page = lazy(() => import('@/pages/workers/dm1-02'));
const DM1_02_02Page = lazy(
	() => import('@/pages/workers/dm1-02/components/dm1-02-2')
);
const DM1_02_03Page = lazy(
	() => import('@/pages/workers/dm1-02/components/dm1-02-3')
);
const DM1_02_05Page = lazy(
	() => import('@/pages/workers/dm1-02/components/dm1-02-5')
);
const DM1_03Page = lazy(() => import('@/pages/workers/dm1-03'));
const DefineDocumentUpload = lazy(
	() => import('@/components/define-document-upload')
);

const DefineDocumentDownloadPage = lazy(
	() => import('@/pages/workers/define-document-download-page')
);

const P_DM1_2 = lazy(() => import('@/pages/workers/dm1-01/components/p-dm1-2'));

const DefineAssignment = lazy(() => import('@/components/define-assignment'));
const DefinePublicAssignment = lazy(
	() => import('@/components/define-public-assignment')
);

const MSelectLodgingPopup = lazy(
	() => import('@/pages/workers/mobile/m-select-lodging-popup')
);
const IssueApplicationDownload = lazy(
	() => import('@/components/issue-application-download')
);
const ST1_01Page = lazy(() => import('@/pages/workers/st1-01'));
const ST1_03Page = lazy(() => import('@/pages/workers/st1-03'));
const WH1_01Page = lazy(() => import('@/pages/workers/wh1-01'));
const DEV_01Page = lazy(() => import('@/pages/workers/dev-01'));
const DEV_03Page = lazy(() => import('@/pages/workers/dev-03'));
const DEV_08Page = lazy(() => import('@/pages/workers/dev-08'));

// foreign
const OD1_01Page = lazy(() => import('@/pages/foreigns/od1-01'));
const OI1_01Page = lazy(() => import('@/pages/foreigns/oi1-01'));
const OI2_01Page = lazy(() => import('@/pages/foreigns/oi2-01'));
const OI2_02Page = lazy(() => import('@/pages/foreigns/oi2-02'));
const OI2_03Page = lazy(() => import('@/pages/foreigns/oi2-03'));
const OR1_01Page = lazy(() => import('@/pages/foreigns/or1-01'));
const OP1_O1Page = lazy(() => import('@/pages/foreigns/op1-01'));
const OP1_O3Page = lazy(() => import('@/pages/foreigns/op1-03'));
const OR1_02Page = lazy(() => import('@/pages/foreigns/or1-02'));
const OR1_03Page = lazy(() => import('@/pages/foreigns/or1-03'));

const MainRouter = () => {
	const {
		toPath,
		isSystemRole,
		isWorkerRole,
		isForeignRole,
		isWorkerPublicAdminRole,
		isWorkerAdminOrPublicAdminRole,
		// isWorkerAdminRole,
	} = useSelector(MyPageSelectors.selectDataMyPage);

	useDocumentTitle();

	return (
		<Routes>
			<Route path="/" element={<MainLayout />}>
				<Route index element={<Navigate to={toPath} replace />} />

				<Route path={ROUTERS.MP1_01} element={<MyPage />} />

				{/* SYSTEM */}
				{isSystemRole && (
					<Route path={ROUTERS.SYSTEM}>
						<Route index path={ROUTERS.UM1_01} element={<UM1_01Page />} />
						<Route path={ROUTERS.CM1_01} element={<CM1_01Page />} />
						<Route path={ROUTERS.CM2_01} element={<CM2_01Page />} />
						<Route
							path={ROUTERS.NM1_01(ROUTERS.SUB_SYSTEM_NOTICE_INQUIRY_BOARD)}
							element={<NM1_01Page />}
						/>
						<Route
							path={ROUTERS.BBM1_01(ROUTERS.SUB_SYSTEM_NOTICE_INQUIRY_BOARD)}
							element={<BBM1_01Page />}
						/>
						<Route path={ROUTERS.SM1_01} element={<SM1_01Page />} />

						<Route
							path={ROUTERS.AU1_01(ROUTERS.SYSTEM)}
							element={<AU1_01Page />}
						/>
					</Route>
				)}

				{/* END SYSTEM*/}

				{/* WORKER */}
				{isWorkerRole && (
					<Route path={ROUTERS.WORKER}>
						<Route path={ROUTERS.ED1_01} element={<ED1_01Page />} />
						{!isWorkerAdminOrPublicAdminRole && (
							<>
								<Route path={ROUTERS.FM1_01} element={<FM1_01Page />} />
								<Route path={ROUTERS.US1_01} element={<US1_01Page />} />
								<Route
									path={ROUTERS.AU1_01(ROUTERS.WORKER)}
									element={<AU1_01Page />}
								/>
							</>
						)}
						<Route path={ROUTERS.MM1_01} element={<MM1_01Page />} />
						<Route path={ROUTERS.AR1_01} element={<AR1_01Page />} />
						<Route path={ROUTERS.FR1_01} element={<FR1_01Page />} />
						<Route path={ROUTERS.PA1_01} element={<PA1_01Page />} />
						<Route path={ROUTERS.PA2_01} element={<PA2_01Page />} />
						<Route path={ROUTERS.PA3_01} element={<PA3_01Page />} />
						<Route path={ROUTERS.PA4_01} element={<PA4_01Page />} />
						{!isWorkerPublicAdminRole && (
							<>
								<Route path={ROUTERS.RM1_01} element={<RM1_01Page />} />
								<Route path={ROUTERS.LM1_01} element={<LM1_01Page />} />
								<Route path={ROUTERS.PM1_01} element={<PM1_01Page />} />
								<Route path={ROUTERS.PM2_01} element={<PM2_01Page />} />
								<Route path={ROUTERS.WC1_01} element={<WC1_01Page />} />
								<Route path={ROUTERS.BM1_01} element={<BM1_01Page />} />
								<Route path={ROUTERS.DM1_01} element={<DM1_01Page />} />
								<Route path={ROUTERS.AM1_01} element={<AM1_01Page />} />
								<Route path={ROUTERS.AM3_01} element={<AM3_01Page />} />
							</>
						)}
						<Route
							path={ROUTERS.NP1_01(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
							element={<NP1_01Page />}
						/>
						<Route
							path={ROUTERS.BB1_01(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
							element={<BB1_01Page />}
						/>
						{!isWorkerPublicAdminRole && (
							<>
								<Route
									path={ROUTERS.NM1_01(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
									element={<NM1_01Page />}
								/>
								<Route
									path={ROUTERS.BBM1_01(
										ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD
									)}
									element={<BBM1_01Page />}
								/>
							</>
						)}
						<Route path={ROUTERS.ST1_01} element={<ST1_01Page />} />
						<Route path={ROUTERS.WH1_01} element={<WH1_01Page />} />
						<Route path={ROUTERS.DEV_01} element={<DEV_01Page />} />
						<Route path={ROUTERS.DEV_03} element={<DEV_03Page />} />
						<Route path={ROUTERS.DEV_08} element={<DEV_08Page />} />
					</Route>
				)}

				{/* END WORKER */}

				{/* FOREIGN */}
				{isForeignRole && (
					<Route path={ROUTERS.FOREIGN}>
						<Route index path={ROUTERS.OD1_01} element={<OD1_01Page />} />
						<Route path={ROUTERS.OI1_01} element={<OI1_01Page />} />
						<Route path={ROUTERS.OI2_01} element={<OI2_01Page />} />
						<Route
							path={ROUTERS.NP1_01(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<NP1_01Page />}
						/>
						<Route
							path={ROUTERS.BB1_01(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<BB1_01Page />}
						/>

						<Route
							path={ROUTERS.NM1_01(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<NM1_01Page />}
						/>
						<Route
							path={ROUTERS.BBM1_01(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<BBM1_01Page />}
						/>
						<Route path={ROUTERS.OR1_01} element={<OR1_01Page />} />
						<Route path={ROUTERS.OP1_01} element={<OP1_O1Page />} />
					</Route>
				)}

				{/* END FOREIGN*/}
			</Route>

			{/* DETAIL ROUTES */}
			<Route element={<CreateDetailLayout />}>
				{isSystemRole && (
					<Route path={ROUTERS.SYSTEM}>
						<Route path={ROUTERS.UM1_02} element={<UM1_02Page />} />
						<Route path={ROUTERS.UM1_03} element={<UM1_03Page />} />
						<Route path={ROUTERS.CM1_02} element={<CM1_02Page />} />
						<Route path={ROUTERS.CM1_03} element={<CM1_03Page />} />
						<Route path={ROUTERS.CM2_02} element={<CM2_02Page />} />
						<Route path={ROUTERS.CM2_03} element={<CM2_03Page />} />
						<Route
							path={ROUTERS.NM1_02(ROUTERS.SUB_SYSTEM_NOTICE_INQUIRY_BOARD)}
							element={<NM1_02Page />}
						/>
						<Route
							path={ROUTERS.NM1_03(ROUTERS.SUB_SYSTEM_NOTICE_INQUIRY_BOARD)}
							element={<NM1_03Page />}
						/>
						<Route
							path={ROUTERS.BBM1_03(ROUTERS.SUB_SYSTEM_NOTICE_INQUIRY_BOARD)}
							element={<BBM1_03Page />}
						/>
						<Route path={ROUTERS.SM1_02} element={<SM1_02Page />} />
						<Route path={ROUTERS.SM1_03} element={<SM1_03Page />} />
					</Route>
				)}

				{isWorkerRole && (
					<Route path={ROUTERS.WORKER}>
						<Route path={ROUTERS.MM1_02} element={<MM1_02Page />} />
						<Route path={ROUTERS.MM1_03} element={<MM1_03Page />} />
						<Route path={ROUTERS.AR1_02} element={<AR1_02Page />} />
						<Route path={ROUTERS.AR1_03} element={<AR1_03Page />} />
						<Route path={ROUTERS.FR1_02} element={<FR1_02Page />} />
						<Route path={ROUTERS.FR1_03} element={<FR1_03Page />} />
						<Route path={ROUTERS.PA1_03} element={<PA1_03Page />} />
						<Route path={ROUTERS.PA3_03} element={<PA3_03Page />} />
						<Route
							path={ROUTERS.DD1_01}
							element={<DefineDocumentDownloadPage />}
						/>
						{!isWorkerPublicAdminRole && (
							<>
								<Route path={ROUTERS.RM1_02} element={<RM1_02Page />} />
								<Route path={ROUTERS.RM1_03} element={<RM1_03Page />} />
								<Route path={ROUTERS.LM1_02} element={<LM1_02Page />} />
								<Route path={ROUTERS.LM1_03} element={<LM1_03Page />} />
								<Route path={ROUTERS.PM1_02} element={<PM1_02Page />} />
								<Route path={ROUTERS.PM1_03} element={<PM1_03Page />} />
								<Route path={ROUTERS.WC1_02} element={<WC1_02Page />} />
								<Route path={ROUTERS.WC1_03} element={<WC1_03Page />} />
								<Route path={ROUTERS.BM1_02} element={<BM1_02Page />} />
								<Route path={ROUTERS.BM1_03} element={<BM1_03Page />} />
								<Route path={ROUTERS.AM1_03} element={<AM1_03Page />} />
							</>
						)}
						{isMobile && (
							<>
								<Route path={ROUTERS.LM2_01} element={<LM2_01Page />} />
								<Route path={ROUTERS.MSLP} element={<MSelectLodgingPopup />} />
								<Route path={ROUTERS.DM1_02} element={<DM1_02Page />} />
								<Route path={ROUTERS.DM1_02_02} element={<DM1_02_02Page />} />
								<Route path={ROUTERS.DM1_02_03} element={<DM1_02_03Page />} />
								<Route path={ROUTERS.DM1_02_05} element={<DM1_02_05Page />} />
								<Route path={ROUTERS.DM1_03} element={<DM1_03Page />} />
							</>
						)}
						<Route
							path={ROUTERS.NP1_03(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
							element={<NP1_03Page />}
						/>
						<Route
							path={ROUTERS.BB1_02(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
							element={<BB1_02Page />}
						/>
						<Route
							path={ROUTERS.BB1_03(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
							element={<BB1_03Page />}
						/>

						{!isWorkerPublicAdminRole && (
							<>
								<Route
									path={ROUTERS.NM1_02(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
									element={<NM1_02Page />}
								/>
								<Route
									path={ROUTERS.NM1_03(ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD)}
									element={<NM1_03Page />}
								/>
								<Route
									path={ROUTERS.BBM1_03(
										ROUTERS.SUB_WORKER_NOTICE_INQUIRY_BOARD
									)}
									element={<BBM1_03Page />}
								/>
							</>
						)}

						<Route path={ROUTERS.ST1_03} element={<ST1_03Page />} />

						<Route
							path={ROUTERS.DEFINE_DOCUMENT_UPLOAD}
							element={<DefineDocumentUpload />}
						/>
						<Route path={ROUTERS.P_DM1_2} element={<P_DM1_2 />} />
						<Route
							path={ROUTERS.DEFINE_ASSIGNMENT}
							element={<DefineAssignment />}
						/>
						<Route
							path={ROUTERS.ISSUE_APPLICATION_DOWNLOAD}
							element={<IssueApplicationDownload />}
						/>
						<Route
							path={ROUTERS.DEFINE_PUBLIC_ASSIGNMENT}
							element={<DefinePublicAssignment />}
						/>
					</Route>
				)}

				{isForeignRole && (
					<Route path={ROUTERS.FOREIGN}>
						<Route path={ROUTERS.OI2_02} element={<OI2_02Page />} />
						<Route path={ROUTERS.OI2_03} element={<OI2_03Page />} />
						<Route path={ROUTERS.OR1_02} element={<OR1_02Page />} />
						<Route path={ROUTERS.OR1_03} element={<OR1_03Page />} />

						<Route path={ROUTERS.OR1_02} element={<OR1_02Page />} />
						<Route path={ROUTERS.OP1_03} element={<OP1_O3Page />} />

						<Route
							path={ROUTERS.NP1_03(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<NP1_03Page />}
						/>
						<Route
							path={ROUTERS.BB1_02(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<BB1_02Page />}
						/>
						<Route
							path={ROUTERS.BB1_03(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<BB1_03Page />}
						/>
						<Route
							path={ROUTERS.NM1_02(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<NM1_02Page />}
						/>
						<Route
							path={ROUTERS.NM1_03(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<NM1_03Page />}
						/>
						<Route
							path={ROUTERS.BBM1_03(ROUTERS.SUB_FOREIGN_NOTICE_INQUIRY_BOARD)}
							element={<BBM1_03Page />}
						/>
					</Route>
				)}
			</Route>
			{/* END DETAIL ROUTES */}

			<Route path="*" element={<NotFoundPage {...{ toPath: '/' }} />} />
		</Routes>
	);
};

export default memo(MainRouter);
